/**
 * Retry a Supabase-style query thunk while it returns an `error`.
 *
 * RETIRE-WHEN (Great Migration N-2): this exists for the auth-critical
 * `user_roles` read in useAuth, which still rides PostgREST. Delete it in the
 * SAME PR that repoints that read to Nest (a14 scope) — the Nest transport has
 * its own retry in src/services/nestRequest.ts.
 *
 * Why: the roles read in useAuth.fetchUserData runs right after a session is
 * established. A transient failure of that first read (a momentarily-expired
 * JWT → 401, a PostgREST/network blip) used to make the caller "bail and keep
 * the session" — which leaves `activeRole` unresolved. The /no-access page's
 * self-heal waits for `activeRole`, so it never fires, and nothing re-reads
 * until an external auth event: the user is stranded on /no-access with only
 * Sign Out, and a page refresh "fixes it". Retrying the read a few times turns
 * that transient blip into a recovered read so the role resolves on its own.
 *
 * Returns the first error-free result, or the last result after `attempts`.
 * Type-preserving: the resolved shape is exactly the query thunk's shape.
 */
export interface RetryQueryOptions<T> {
  /** Total attempts including the first (default 3). */
  attempts?: number;
  /** Delay between attempts in ms (default 400). */
  delayMs?: number;
  /**
   * Whether a given result warrants another attempt. Default: retry while the
   * result carries an `error`. Callers can widen this — e.g. the roles read
   * also retries an EMPTY-but-error-free result, because right after a session
   * is established the PostgREST/RLS context can momentarily resolve to zero
   * rows for a user who DOES have roles, and acting on that empty read would
   * force-log-out a valid session.
   */
  shouldRetry?: (result: T) => boolean;
  /** Injectable sleep for tests (defaults to setTimeout). */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function retryOnQueryError<T extends { error: unknown }>(
  // PromiseLike, not Promise: a Supabase query builder is a thenable, not a
  // real Promise — accepting PromiseLike lets callers pass the builder directly.
  run: () => PromiseLike<T>,
  {
    attempts = 3,
    delayMs = 400,
    shouldRetry = (r: T) => !!r.error,
    sleep = defaultSleep,
  }: RetryQueryOptions<T> = {},
): Promise<T> {
  let result = await run();
  for (let attempt = 1; attempt < attempts && shouldRetry(result); attempt++) {
    await sleep(delayMs);
    result = await run();
  }
  return result;
}
